import { useState, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Header,
  type Row,
  type SortingState,
} from '@tanstack/react-table';
import './DataTable.css';

export type { ColumnDef } from '@tanstack/react-table';

export interface DataTableProps<TRow> {
  columns: ColumnDef<TRow, unknown>[];
  data: TRow[];
  /** Spoken name for the table. */
  caption: string;
  /** Rendered when data is empty; usually an <EmptyState compact />. */
  empty?: ReactNode;
  onRowClick?: (row: TRow) => void;
  getRowId?: (row: TRow) => string;
  dense?: boolean;
}

/**
 * A thin wrapper over TanStack Table for admin grids: sortable headers, optional row click,
 * an empty slot. Filtering, pagination and selection are added where a screen needs them.
 */
export function DataTable<TRow>(props: DataTableProps<TRow>): ReactNode {
  const { columns, caption, empty, onRowClick, dense } = props;
  const table = useSortedTable(props);
  const rows = table.getRowModel().rows;
  return (
    <div className={['pui-table-wrap', dense && 'pui-table--dense'].filter(Boolean).join(' ')}>
      <table className="pui-table">
        <caption className="pui-visually-hidden">{caption}</caption>
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <HeaderCell key={h.id} header={h} />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="pui-table__empty">
                {empty ?? 'Nothing here yet.'}
              </td>
            </tr>
          ) : (
            rows.map((row) => <DataRow key={row.id} row={row} onRowClick={onRowClick} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

function useSortedTable<TRow>({ data, columns, getRowId }: DataTableProps<TRow>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  return useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(getRowId ? { getRowId } : {}),
  });
}

type SortDir = false | 'asc' | 'desc';

function sortIndicator(dir: SortDir): string {
  if (dir === 'asc') return ' ↑';
  if (dir === 'desc') return ' ↓';
  return '';
}

function ariaSort(dir: SortDir): 'ascending' | 'descending' | 'none' {
  if (dir === 'asc') return 'ascending';
  if (dir === 'desc') return 'descending';
  return 'none';
}

function HeaderCell<TRow>({ header }: { header: Header<TRow, unknown> }): ReactNode {
  const sortable = header.column.getCanSort();
  const dir = header.column.getIsSorted();
  const content = flexRender(header.column.columnDef.header, header.getContext());
  return (
    <th scope="col" aria-sort={sortable ? ariaSort(dir) : undefined}>
      {sortable ? (
        <button
          type="button"
          className="pui-table__sort"
          onClick={header.column.getToggleSortingHandler()}
        >
          {content}
          {sortIndicator(dir)}
        </button>
      ) : (
        content
      )}
    </th>
  );
}

function DataRow<TRow>({
  row,
  onRowClick,
}: {
  row: Row<TRow>;
  onRowClick?: ((row: TRow) => void) | undefined;
}): ReactNode {
  const clickable = Boolean(onRowClick);
  return (
    <tr
      className={clickable ? 'pui-table__row--clickable' : undefined}
      onClick={clickable ? () => onRowClick?.(row.original) : undefined}
      onKeyDown={clickable ? (e) => e.key === 'Enter' && onRowClick?.(row.original) : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {row.getVisibleCells().map((cell) => (
        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
      ))}
    </tr>
  );
}
